import { useEffect, useMemo, useState } from 'react';
import {
  type CountryCode,
  type EntityType,
  type KycDepth,
  type KycStatus,
  type SanctionsStatus,
  type Sector,
} from '@shared';
import { apiGet, apiPost, ApiError } from '../../lib/api';
import { ComplianceTabs } from './ComplianceTabs';

// Compliance · KYC register screen. Lists every client with their KYC +
// sanctions state and exposes the same actions Compliance leans on in
// production — approve, request refresh, raise a sanctions flag, mark current.
// Lives under the consolidated Compliance area as the "KYC register" tab,
// alongside the document-review queue.
//
// Calls the existing PATCH /api/clients/:id/kyc endpoint which already has
// 'kyc:write' RBAC + writes an audit row per change.

interface ClientRow {
  id: string;
  name: string;
  entity_type: EntityType;
  registration_number: string;
  country: CountryCode;
  sector: Sector;
  kyc: KycStatus;
  kyc_depth?: KycDepth;
  sanctions_status?: SanctionsStatus;
  status?: 'active' | 'suspended' | 'deactivated';
  open_requests?: number;
}

// PATCH uses fetch directly because the existing apiPatch lives in api.ts and
// we want the same shape — this is just a wrapper for clarity.
async function patchKyc(id: string, body: Record<string, unknown>): Promise<ClientRow> {
  const r = await fetch(`/api/clients/${id}/kyc`, {
    method: 'PATCH',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  const json = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, json);
  return (json as { client: ClientRow }).client;
}

const KYC_TONE: Record<KycStatus, string> = {
  current: 'green',
  refresh: 'amber',
  pending: 'amber',
  in_review: 'accent',
  expired: 'red',
  rejected: 'red',
};

const KYC_LABEL: Record<KycStatus, string> = {
  current: 'Current',
  refresh: 'Refresh due',
  pending: 'Pending',
  in_review: 'In review',
  expired: 'Expired',
  rejected: 'Rejected',
};

type Filter = 'all' | 'attention' | KycStatus;

export function AdminKyc() {
  const [rows, setRows] = useState<ClientRow[] | null>(null);
  const [filter, setFilter] = useState<Filter>('attention');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiGet<{ clients: ClientRow[] }>('/api/clients')
      .then((r) => setRows(r.clients))
      .catch((err: Error) => setError(err instanceof ApiError ? err.serverMessage : err.message));
  }, []);

  const counts = useMemo(() => {
    const c: Record<KycStatus, number> = { current: 0, refresh: 0, pending: 0, in_review: 0, expired: 0, rejected: 0 };
    let flagged = 0;
    for (const r of rows ?? []) {
      c[r.kyc] = (c[r.kyc] ?? 0) + 1;
      if (r.sanctions_status === 'flagged') flagged++;
    }
    return { ...c, flagged };
  }, [rows]);

  const list = useMemo(() => {
    const all = rows ?? [];
    if (filter === 'all') return all;
    if (filter === 'attention') return all.filter((r) => r.kyc !== 'current' || r.sanctions_status === 'flagged');
    return all.filter((r) => r.kyc === filter);
  }, [rows, filter]);

  async function applyKyc(client: ClientRow, patch: { kyc?: KycStatus; sanctions_status?: SanctionsStatus }) {
    setBusyId(client.id);
    setError(null);
    try {
      const updated = await patchKyc(client.id, patch);
      setRows((prev) => (prev ?? []).map((c) => (c.id === client.id ? { ...c, ...updated } : c)));
    } catch (err) {
      setError(err instanceof ApiError ? err.serverMessage : 'KYC update failed.');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <>
      <ComplianceTabs />
      <div>
        <h1 className="portal-h1">KYC register</h1>
        <p className="portal-sub">
          Body-corporate KYC + sanctions register. Update status here when refresh packs come back
          from clients — every action writes an audit row (REQ-007).
        </p>
      </div>

      <div className="portal-cards">
        <div className="card">
          <div className="lbl">Current</div>
          <div className="val">{counts.current}</div>
          <div className="delta">Clear for engagement</div>
        </div>
        <div className="card">
          <div className="lbl">Refresh due</div>
          <div className="val">{counts.refresh + counts.pending}</div>
          <div className="delta">Pack outstanding</div>
        </div>
        <div className="card">
          <div className="lbl">Expired / rejected</div>
          <div className="val">{counts.expired + counts.rejected}</div>
          <div className="delta">Cannot engage</div>
        </div>
        <div className="card">
          <div className="lbl">Sanctions flagged</div>
          <div className="val" style={{ color: counts.flagged > 0 ? 'var(--red)' : undefined }}>{counts.flagged}</div>
          <div className="delta">Escalate to Compliance</div>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 24 }}>
        <button className={filter === 'attention' ? 'on' : ''} onClick={() => setFilter('attention')}>Needs attention</button>
        <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>All</button>
        <button className={filter === 'current' ? 'on' : ''} onClick={() => setFilter('current')}>Current</button>
        <button className={filter === 'refresh' ? 'on' : ''} onClick={() => setFilter('refresh')}>Refresh</button>
        <button className={filter === 'pending' ? 'on' : ''} onClick={() => setFilter('pending')}>Pending</button>
        <button className={filter === 'expired' ? 'on' : ''} onClick={() => setFilter('expired')}>Expired</button>
      </div>

      {error && (
        <div className="card" style={{ marginTop: 16, borderColor: 'var(--red)', color: 'var(--red)' }}>
          {error}
        </div>
      )}

      <div className="card flush">
        <table className="tbl">
          <thead>
            <tr>
              <th>Client</th>
              <th>Country</th>
              <th>KYC</th>
              <th>Sanctions</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows === null && (
              <tr><td colSpan={5} style={{ color: 'var(--fg-mute)', fontSize: 13 }}>Loading…</td></tr>
            )}
            {rows !== null && list.length === 0 && (
              <tr>
                <td colSpan={5} style={{ color: 'var(--fg-mute)', fontSize: 13, textAlign: 'center', padding: 32 }}>
                  No clients in this view.
                </td>
              </tr>
            )}
            {list.map((c) => (
              <tr key={c.id}>
                <td>
                  <div style={{ fontWeight: 500 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--fg-mute)', fontFamily: 'var(--mono)' }}>
                    {c.id} · {c.entity_type}
                  </div>
                </td>
                <td style={{ color: 'var(--fg-soft)', fontSize: 13 }}>{c.country}</td>
                <td>
                  <span className={`chip ${KYC_TONE[c.kyc]}`}>
                    <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'currentColor' }} />
                    {KYC_LABEL[c.kyc]}
                  </span>
                </td>
                <td style={{ fontSize: 13 }}>
                  {c.sanctions_status === 'flagged' ? (
                    <span style={{ color: 'var(--red)', fontWeight: 500 }}>Flagged</span>
                  ) : c.sanctions_status === 'clear' ? (
                    <span style={{ color: 'var(--fg-soft)' }}>Clear</span>
                  ) : (
                    <span style={{ color: 'var(--fg-mute)' }}>Pending</span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6 }}>
                    <button
                      type="button"
                      className="btn ghost sm"
                      disabled={busyId === c.id || c.kyc === 'current'}
                      onClick={() => applyKyc(c, { kyc: 'current' })}
                    >Mark current</button>
                    <button
                      type="button"
                      className="btn ghost sm"
                      disabled={busyId === c.id || c.kyc === 'refresh'}
                      onClick={() => applyKyc(c, { kyc: 'refresh' })}
                    >Refresh</button>
                    {c.sanctions_status !== 'flagged' && (
                      <button
                        type="button"
                        className="btn ghost sm"
                        disabled={busyId === c.id}
                        onClick={() => applyKyc(c, { sanctions_status: 'flagged' })}
                        style={{ color: 'var(--red)' }}
                      >Flag</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
